import { InputAdornment } from '@mui/material'
import {
  DateTimeValidationError,
  LocalizationProvider,
  PickerChangeHandlerContext,
  PickersShortcutsItem,
} from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import {
  DateRange as MuiDateRange,
  DateRangePicker,
  DateRangePickerProps,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro'
import { DateTime } from 'luxon'
import React, { useState } from 'react'

import { useComplatibleLocale } from '../../hooks/useCompatibleLocale'
import { useRtlLocaleCheck } from '../../hooks/useRtlLocaleCheck'
import { styled } from '../../theme'
import { THEME_CONST } from '../../theme'
import { MsdkStyledComponent } from '../../utils'
import { ButtonIcon } from '../Button'
import { IconSelector } from '../Icons'

const StyledDateRangePicker: MsdkStyledComponent<DateRangePickerProps<any>> = styled(DateRangePicker)`
  .MuiInputBase-root {
    border-radius: ${THEME_CONST.inputBorderRadius}px;
    width: 100%;
  }
  .MuiInputBase-input {
    padding: 9px;
    width: 100%;
  }
  .MuiFormControl-root {
    width: 100%;
  }
`

export type DateRangePickerShortcuts = PickersShortcutsItem<MuiDateRange<DateTime>>[]
export type DateRange = MuiDateRange<DateTime<boolean> | null>

type Props = {
  disabled?: boolean
  label: React.ReactNode
  locale?: string
  onChange: (value: DateRange | undefined, context?: PickerChangeHandlerContext<DateTimeValidationError>) => void
  shortcuts?: PickersShortcutsItem<MuiDateRange<DateTime>>[]
  value: DateRange | undefined
}

export function RangeDatePicker(props: Props) {
  const { disabled, label, locale = DateTime.local().locale, onChange, shortcuts, value } = props

  const [open, setOpen] = useState<boolean>(false)
  const compatibleLocale = useComplatibleLocale(locale)
  const isRtl = useRtlLocaleCheck(locale)

  function handleDateChange(newDates: DateRange | undefined) {
    if (newDates) {
      onChange?.(newDates)
    }
  }

  function handleOpen() {
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
  }

  function handleIconClick() {
    setOpen((prev) => !prev)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={compatibleLocale}>
      <StyledDateRangePicker
        disabled={disabled}
        label={label}
        slots={{
          field: SingleInputDateRangeField,
        }}
        slotProps={{
          field: {
            readOnly: true,
            disabled,
          },
          textField: {
            onClick: () => null,
            InputProps: {
              readOnly: true,
              dir: isRtl ? 'rtl' : undefined,
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                  <ButtonIcon onClick={handleIconClick}>
                    <IconSelector iconName="DateRange" color="action" />
                  </ButtonIcon>
                </InputAdornment>
              ),
            },
          },
          shortcuts: {
            items: shortcuts,
          },
          actionBar: { actions: [] },
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        calendars={2}
        value={value}
        onChange={handleDateChange}
        maxDate={DateTime.now()}
      />
    </LocalizationProvider>
  )
}
